<script setup>
import { SidebarMenu } from 'vue-sidebar-menu';
import 'vue-sidebar-menu/dist/vue-sidebar-menu.css';
import { useDisplay } from 'vuetify';
import { useAuthStore } from '@/stores/authorization';
import { useSettingsStore } from '@/stores/settings';
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';
import { navigationGuard } from '@/router/navigation-guard';

const { xs } = useDisplay();
const { toggleSidebar } = useSettingsStore();
const { sidebarCollapse, theme } = storeToRefs(useSettingsStore());
const { authState } = storeToRefs(useAuthStore());
const { t } = useI18n();

const menuWidth = 300;

const isVisible = routeName => {
  const routeClaims = [];
  if (typeof routeName === 'string') {
    const guard = navigationGuard[routeName] || [];
    routeClaims.push(...guard);
  }
  if (Array.isArray(routeName)) {
    routeName.forEach(route => {
      const guard = navigationGuard[route];
      if (!guard) {
        console.warn(`Guard definition missing for route ${route}`);
        return;
      }
      guard.forEach(claim => {
        if (routeClaims.includes(claim)) return;
        routeClaims.push(claim);
      });
    });
  }
  if (!routeClaims.length) return false;
  return authState.value.user.claims.some(claim => routeClaims.includes(claim));
};

const beneficiariesList = isVisible(['beneficiary'])
  ? [
      {
        title: t('sidebar.beneficiariesList'),
        href: { name: 'beneficiariesList' },
        icon: 'mdi mdi-account-supervisor',
      },
    ]
  : [];
const beneficiariesExport = isVisible(['beneficiary-export'])
  ? [
      {
        title: t('sidebar.beneficiariesExport'),
        href: { name: 'beneficiariesExport' },
        icon: 'mdi mdi-download',
      },
    ]
  : [];
const beneficiariesReissueExport = isVisible(['beneficiary-export'])
  ? [
      {
        title: t('sidebar.beneficiariesReissueExport'),
        href: { name: 'beneficiariesReissueExport' },
        icon: 'mdi mdi-download-multiple',
        class: 'highlight-item',
      },
    ]
  : [];
const beneficiariesImport = isVisible(['beneficiary-import'])
  ? [
      {
        title: t('sidebar.beneficiariesLoad'),
        href: { name: 'beneficiariesImport' },
        icon: 'mdi mdi-upload',
      },
    ]
  : [];
const beneficiariesUpdate = isVisible(['beneficiary-update'])
  ? [
      {
        title: t('sidebar.beneficiariesUpdate'),
        href: { name: 'beneficiariesUpdate' },
        icon: 'mdi mdi-account-card-outline',
      },
    ]
  : [];
const requestActivation = isVisible(['request-activation'])
  ? [
      {
        title: t('sidebar.requestActivation'),
        href: { name: 'requestActivation' },
        icon: 'mdi mdi-account-reactivate-outline',
      },
    ]
  : [];
const requestDenial = isVisible(['request-denial'])
  ? [
      {
        title: t('sidebar.requestDenial'),
        href: { name: 'requestDenial' },
        icon: 'mdi mdi-account-off-outline',
      },
    ]
  : [];
const beneficiariesReissue = isVisible(['beneficiary-reissue'])
  ? [
      {
        title: t('sidebar.beneficiariesCardReissue'),
        href: { name: 'beneficiariesReissue' },
        icon: 'mdi mdi-account-card-outline',
        class: 'highlight-item',
      },
    ]
  : [];
const beneficiariesMenu = isVisible(['beneficiary', 'beneficiary-import'])
  ? [
      {
        title: t('sidebar.beneficiaries'),
        icon: 'mdi mdi-account-cog',
        child: [
          ...beneficiariesExport,
          ...beneficiariesReissueExport,
          ...beneficiariesImport,
          ...beneficiariesUpdate,
          ...requestActivation,
          ...requestDenial,
          ...beneficiariesReissue,
        ],
      },
    ]
  : [];
const issueMenu = isVisible(['issues'])
  ? [
      {
        title: t('sidebar.issues'),
        href: { name: 'issueList' },
        icon: 'mdi mdi-alert',
      },
    ]
  : [];

const menu = [
  {
    title: t('sidebar.dashboard'),
    href: { name: 'dashboard' },
    icon: 'mdi mdi-view-dashboard',
  },
  ...beneficiariesList,
  ...beneficiariesMenu,
  ...issueMenu,
];
</script>
<template>
  <sidebar-menu
    v-if="!xs || (xs && !sidebarCollapse)"
    :menu="menu"
    :collapsed="xs ? false : sidebarCollapse"
    :theme="theme === 'light' ? 'white-theme' : ''"
    :width="menuWidth + 'px'"
    @update:collapsed="toggleSidebar"
  />
</template>
<style lang="scss">
.v-sidebar-menu.vsm_white-theme {
  background-color: #f2f2f2;
}
.v-sidebar-menu.vsm_white-theme .vsm--link:hover {
  color: lightgray;
  background-color: #184a86;
}
.v-sidebar-menu.vsm_white-theme .vsm--link:hover > i {
  color: lightgray;
  background-color: #184a86;
}
.v-sidebar-menu.vsm_white-theme.vsm_collapsed .vsm--link.vsm--link_mobile-item.vsm--link_active {
  color: lightgray;
  background-color: #184a86;
}
.v-sidebar-menu.vsm_white-theme .vsm--toggle-btn {
  border-top: 1px solid lightgray;
}
.v-sidebar-menu.vsm_white-theme .vsm--link_level-1 .vsm--icon {
  background-color: transparent;
}
.v-sidebar-menu.vsm_white-theme .vsm--link_level-1.vsm--link_active .vsm--icon {
  background-color: #184a86;
}
.v-sidebar-menu.vsm_white-theme.vsm_expanded .vsm--link_level-1.vsm--link_open {
  .vsm--icon {
    background-color: #184a86;
  }
  background-color: #184a86;
}
.v-sidebar-menu.vsm_white-theme.vsm_collapsed .vsm--link_level-1.vsm--link_open {
  .vsm--icon {
    background-color: #184a86;
  }
  background-color: #184a86;
}
.v-sidebar-menu.vsm_white-theme.vsm_collapsed .vsm--link.vsm--link_mobile-item .vsm--icon {
  background-color: #184a86;
}
.v-sidebar-menu.vsm_white-theme.vsm_collapsed .vsm--link.vsm--link_mobile-item.vsm--link_active {
  background-color: #184a86;
}
.highlight-item {
  color: #c8113f !important;
  text-wrap: wrap;
  line-height: 1.3 !important;
}
</style>
